import React, { useEffect, useRef, useState } from "react";

import { CSSObject, MantineColor, Text, TextInput, Title, useMantineTheme } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";

import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { useAppState } from "../../../hooks/useAppState";
import { EditableString } from "../../../lib/model/checklistAppState";
import { autofocusFieldSlice } from "../../../lib/state/reducer";

type InlineEditableTextProps = {
  text: EditableString;
  color?: MantineColor;
  placeholder: string;
  styles?: CSSObject;
  extraPadding?: number;
  justify?: "left" | "right" | "center";
  onSave: (newValue: string) => void;
  onEmptyEnter?: () => void;
  onEmptyTab?: () => void;
  onEmptyBlur?: () => void;
};

const withEditable = <T,>(
  TextComponent: React.ComponentType<T> | ((props: T) => React.ReactElement<T>)
) => {
  return (iprops: T & InlineEditableTextProps) => {
    const {
      text,
      color,
      placeholder,
      styles,
      extraPadding,
      justify,
      onSave,
      onEmptyEnter,
      onEmptyTab,
      onEmptyBlur,
      ...props
    } = iprops;
    const [newValue, setNewValue] = useState<string>(text.content);
    const [inputStyle, setInputStyle] = useState<CSSObject>({});
    // const inputWidthRef = useRef<HTMLElement>(null);
    const { ref: inputWidthRef, width: measuredWidth } = useElementSize();
    const inputRef = useRef<HTMLInputElement>(null);
    const autofocusField = useAppState((state) => state.autofocusField);
    const reduxDispatch = useAppDispatch();

    const extraPaddingWidth = extraPadding || 0;

    const mantineTheme = useMantineTheme();

    const textColor = color || mantineTheme.black;

    const saveAndFinishEditing = React.useCallback(() => {
      onSave(newValue);
      inputRef.current?.blur();
    }, [onSave, newValue, inputRef.current]);

    const cancelEditing = React.useCallback(() => {
      setNewValue(text.content);
      inputRef.current?.blur();
    }, [setNewValue, inputRef.current]);

    const qualifyEmptyContinue = text.content === "" && newValue !== text.content;
    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = React.useCallback(
      (event) => {
        if (event.key === "Enter") {
          saveAndFinishEditing();
          if (qualifyEmptyContinue && onEmptyEnter) {
            onEmptyEnter();
          }
        } else if (event.key === "Escape") {
          cancelEditing();
        } else if (event.key === "Tab" && qualifyEmptyContinue && onEmptyTab) {
          saveAndFinishEditing();
          onEmptyTab();
          event.preventDefault();
        }
      },
      [
        text.content,
        qualifyEmptyContinue,
        saveAndFinishEditing,
        onEmptyEnter,
        cancelEditing,
        onEmptyTab,
      ]
    );

    useEffect(() => {
      setNewValue(text.content);
    }, [text.content]);

    useEffect(() => {
      if (inputWidthRef.current) {
        const refStyle = getComputedStyle(inputWidthRef.current);
        setInputStyle({
          ...inputStyle,
          fontSize: refStyle.fontSize,
          fontWeight: refStyle.fontWeight as CSSObject["fontWeight"],
          lineHeight: refStyle.lineHeight,
          color: textColor,
          "&:focus": {
            borderBottom: `1px dashed ${textColor}`,
            cursor: "text",
          },
        });
      }
    }, [inputWidthRef.current]);

    useEffect(() => {
      if (autofocusField === text.id) {
        inputRef.current?.focus();
        reduxDispatch(autofocusFieldSlice.actions.setAutofocusField(null));
      }
    }, [autofocusField, text.id, inputRef.current]);

    const onChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => setNewValue(e.target.value),
      [setNewValue]
    );

    const onBlur = React.useCallback(() => {
      saveAndFinishEditing();
      if (newValue === "" && onEmptyBlur) {
        onEmptyBlur();
      }
    }, [newValue, onEmptyBlur]);

    const displayText = newValue || placeholder;
    const calculatedWidth = `${(measuredWidth || 39 * displayText.length) + extraPaddingWidth}px`;

    return (
      <TextInput
        autoFocus={autofocusField === text.id}
        ref={inputRef}
        variant="unstyled"
        inputContainer={(c) => (
          <>
            <TextComponent
              ref={inputWidthRef}
              style={{
                position: "absolute",
                opacity: 0,
                zIndex: -100,
                whiteSpace: "pre",
                padding: 0,
                margin: 0,
                border: 0,
              }}
              {...(props as unknown as T)}
            >
              {displayText}
            </TextComponent>
            {c}
          </>
        )}
        styles={{
          root: { flex: "0 0 auto" },
          wrapper: { flex: "0 0 auto" },
          input: {
            ...inputStyle,
            ...styles,
            textAlign: justify,
            height: "1.5em",
            padding: 0,
            minWidth: "20px",
            "&:focus:hover": {
              cursor: "text",
            },
            "&:hover": {
              cursor: "pointer",
            },
          },
        }}
        style={{
          width: calculatedWidth,
        }}
        onChange={onChange}
        value={newValue}
        placeholder={placeholder}
        spellCheck={false}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  };
};

export const InlineEditableTitle = withEditable(Title);
export const InlineEditableText = withEditable(Text<"div">);
